import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getDeposits, confirmDeposit, refundDeposit,
  getPixReceiveSettings, updatePixReceiveSettings,
  getAutoConfirmStatus, activateAutoConfirm,
} from '../../services/api';
import MoneyInput from '../../components/MoneyInput';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatDateTime } from '../../utils/formatDateTime';
import { formatMoneyBRL } from '../../components/MoneyInput';
import PageTour from '../../components/tenant/PageTour';

const TOUR_STEPS = [
  {
    selector: '[data-tour="deposits-tabs"]',
    title: 'Recebimentos do seu negócio',
    content: 'Aqui é o PIX que os seus CLIENTES te pagam (sinal de agendamento). Diferente do "Financeiro" — aquele é o que você paga pro AtendoApp.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="deposits-tab-dashboard"]',
    title: 'Sinais recebidos',
    content: 'Lista tudo que foi cobrado: pendentes, confirmados, reembolsados, expirados — com totais em R$.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="deposits-tab-pix"]',
    title: 'Configure sua chave PIX',
    content: 'Cadastre a chave PIX do seu negócio. Quando um cliente agendar um serviço com sinal, ele recebe um QR code pra pagar direto pra você.',
    placement: 'bottom',
  },
];

/**
 * Recebimentos — PIX de sinal pago pelos clientes finais ao tenant.
 *
 * Duas abas:
 *   - Dashboard: listagem dos sinais com filtros por status + totais
 *   - Configuração: chave PIX + tempo de expiração
 *
 * Este fluxo NÃO é o mesmo do Financeiro (que é o tenant pagando a AtendoApp).
 * Aqui é o tenant RECEBENDO dos clientes dele. Dinheiro cai direto na conta
 * do tenant, AtendoApp não intermedia.
 */

const STATUS_META = {
  pending:  { label: 'Aguardando pgto', color: '#f59e0b', bg: '#fef3c7' },
  paid:     { label: 'Confirmado',      color: '#16a34a', bg: '#dcfce7' },
  expired:  { label: 'Expirado',        color: '#94a3b8', bg: '#f1f5f9' },
  refunded: { label: 'Reembolsado',     color: '#64748b', bg: '#e2e8f0' },
};

const KEY_TYPE_OPTIONS = [
  { value: 'cpf',    label: 'CPF' },
  { value: 'cnpj',   label: 'CNPJ' },
  { value: 'email',  label: 'E-mail' },
  { value: 'phone',  label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' },
];

export default function TenantDeposits() {
  const { hasPermission } = useAuth();
  const confirm = useConfirm();
  const [tab, setTab] = useState('dashboard');

  // ── Dashboard state ─────────────────────────────────────────────────────
  const [deposits, setDeposits] = useState([]);
  const [stats, setStats]       = useState({});
  const [statusF, setStatusF]   = useState('');
  const [loading, setLoading]   = useState(true);

  // ── Config state ────────────────────────────────────────────────────────
  const canManage = hasPermission('MANAGE_TENANT_SETTINGS');
  const [cfg, setCfg] = useState({
    pix_key: '', pix_key_type: 'cpf', pix_holder_name: '',
    pix_city: '', deposit_expire_minutes: 60, configured: false,
  });
  const [cfgLoading, setCfgLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadDeposits = useCallback(() => {
    setLoading(true);
    getDeposits({ status: statusF || undefined, limit: 100 })
      .then(r => { setDeposits(r.data.deposits || []); setStats(r.data.stats || {}); })
      .catch(() => toast.error('Erro ao carregar sinais'))
      .finally(() => setLoading(false));
  }, [statusF]);

  const loadCfg = useCallback(() => {
    setCfgLoading(true);
    getPixReceiveSettings()
      .then(r => setCfg(c => ({ ...c, ...r.data })))
      .catch(() => toast.error('Erro ao carregar configuração PIX'))
      .finally(() => setCfgLoading(false));
  }, []);

  useEffect(() => { if (tab === 'dashboard') loadDeposits(); }, [tab, loadDeposits]);
  useEffect(() => { if (tab === 'config') { loadCfg(); loadAutoConfirm(); } }, [tab, loadCfg]);

  // ── Auto-confirm state ─────────────────────────────────────────────────
  const [autoConfirm, setAutoConfirm] = useState({ active: false, status: null, data: {} });
  const [autoForm, setAutoForm] = useState({
    cpf_cnpj: '', birth_date: '', company_type: 'MEI', income_value: 0,
    address_street: '', address_number: '', address_complement: '',
    address_neighborhood: '', address_postal_code: '', address_state: '', address_city: '',
  });
  const [activating, setActivating] = useState(false);

  const loadAutoConfirm = useCallback(() => {
    getAutoConfirmStatus()
      .then(r => {
        setAutoConfirm(r.data);
        if (r.data.data) setAutoForm(f => ({ ...f, ...r.data.data }));
      })
      .catch(() => {});
  }, []);

  const handleActivate = async (e) => {
    e.preventDefault();
    setActivating(true);
    try {
      const r = await activateAutoConfirm(autoForm);
      toast.success(r.data.message || 'Ativado!');
      loadAutoConfirm();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao ativar');
    } finally { setActivating(false); }
  };

  const COMPANY_TYPES = [
    { value: 'MEI', label: 'MEI (Microempreendedor Individual)' },
    { value: 'LIMITED', label: 'LTDA (Empresa Limitada)' },
    { value: 'INDIVIDUAL', label: 'Autônomo / Pessoa Física' },
    { value: 'ASSOCIATION', label: 'Associação' },
  ];
  const UF_LIST = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

  // ── Ações ───────────────────────────────────────────────────────────────
  const handleConfirm = async (d) => {
    const ok = await confirm({
      title: 'Confirmar recebimento?',
      message: `Você recebeu R$ ${Number(d.amount).toFixed(2).replace('.', ',')} de ${d.client_name}? Confirme apenas depois de ver o valor na sua conta.`,
      confirmText: 'Confirmar recebimento',
      variant: 'primary',
    });
    if (!ok) return;
    try {
      await confirmDeposit(d.id);
      toast.success('Sinal confirmado — agendamento liberado.');
      loadDeposits();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao confirmar');
    }
  };

  const handleRefund = async (d) => {
    const ok = await confirm({
      title: 'Marcar como reembolsado?',
      message: 'Use esta opção se você já devolveu o valor pro cliente. Isso não move dinheiro — é só marcação.',
      confirmText: 'Marcar reembolsado',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await refundDeposit(d.id);
      toast.success('Marcado como reembolsado.');
      loadDeposits();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao marcar');
    }
  };

  const handleSaveCfg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updatePixReceiveSettings(cfg);
      toast.success('Configuração salva.');
      loadCfg();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const tabBtn = (name, label) => (
    <button
      onClick={() => setTab(name)}
      style={{
        padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        background: tab === name ? 'var(--primary)' : 'transparent',
        color: tab === name ? 'white' : 'var(--gray-600)',
        border: tab === name ? '1.5px solid var(--primary)' : '1.5px solid var(--gray-200)',
        borderRadius: 8, fontFamily: 'inherit',
      }}
    >{label}</button>
  );

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Recebimentos</h2>
          <p>Sinais via PIX pagos pelos seus clientes na hora do agendamento.</p>
          <div style={{ marginTop: 8 }}>
            <PageTour tourId="deposits" steps={TOUR_STEPS} />
          </div>
        </div>
      </div>

      <div data-tour="deposits-tabs" style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <span data-tour="deposits-tab-dashboard">{tabBtn('dashboard', '📊 Sinais recebidos')}</span>
        <span data-tour="deposits-tab-pix">{tabBtn('config', '⚙️ Chave PIX')}</span>
      </div>

      {/* Aviso se chave ainda não configurada */}
      {tab === 'dashboard' && !cfgLoading && !cfg.configured && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#92400e',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span>⚠️ Você ainda não cadastrou sua chave PIX — ative os sinais clicando em <strong>Chave PIX</strong> acima.</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setTab('config')}>Configurar</button>
        </div>
      )}

      {/* ─────────── Dashboard (lista) ─────────── */}
      {tab === 'dashboard' && (
        <>
          {/* Stats cards */}
          {Object.keys(stats).length > 0 && (
            <div className="stats-grid" style={{ marginBottom: 16 }}>
              {['pending', 'paid', 'refunded', 'expired'].map(s => {
                const st = stats[s] || { count: 0, amount: 0 };
                const meta = STATUS_META[s];
                return (
                  <div key={s} className="stat-card">
                    <div className="stat-icon" style={{ background: meta.bg, color: meta.color }}>
                      {s === 'pending' ? '⏳' : s === 'paid' ? '✅' : s === 'refunded' ? '↩️' : '⌛'}
                    </div>
                    <div>
                      <div className="stat-label">{meta.label}</div>
                      <div className="stat-value" style={{ fontSize: 18 }}>
                        {formatMoneyBRL(st.amount)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{st.count} sinal(is)</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Filtros de status */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => setStatusF('')}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                border: `1.5px solid ${statusF === '' ? 'var(--primary)' : 'var(--gray-200)'}`,
                background: statusF === '' ? 'var(--primary)' : 'transparent',
                color: statusF === '' ? 'white' : 'var(--gray-700)',
                cursor: 'pointer',
              }}>Todos</button>
            {Object.entries(STATUS_META).map(([s, meta]) => (
              <button
                key={s}
                onClick={() => setStatusF(s)}
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                  border: `1.5px solid ${statusF === s ? meta.color : 'var(--gray-200)'}`,
                  background: statusF === s ? meta.color : 'transparent',
                  color: statusF === s ? 'white' : meta.color,
                  cursor: 'pointer',
                }}>{meta.label}</button>
            ))}
          </div>

          {loading ? (
            <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
          ) : deposits.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💳</div>
              <h3>Nenhum sinal recebido ainda</h3>
              <p>Quando um cliente agendar um serviço com sinal, o QR aparece aqui automaticamente.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {deposits.map(d => {
                const meta = STATUS_META[d.status] || STATUS_META.pending;
                const expired = d.status === 'pending' && d.expires_at && new Date(d.expires_at) < new Date();
                return (
                  <div key={d.id} className="mobile-card" style={{
                    background: 'var(--white)', borderRadius: 10,
                    border: '1px solid var(--gray-200)', borderLeft: `4px solid ${meta.color}`,
                    padding: 14, display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{d.client_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                          {d.service_name || 'Serviço'} · {formatDateTime(d.appointment_datetime)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--gray-900)' }}>
                          {formatMoneyBRL(d.amount)}
                        </div>
                        <span style={{
                          display: 'inline-block', marginTop: 4,
                          background: meta.bg, color: meta.color, fontSize: 10,
                          padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: 0.3,
                        }}>{meta.label}</span>
                      </div>
                    </div>

                    {d.status === 'pending' && (
                      <div style={{ fontSize: 12, color: expired ? '#b45309' : 'var(--gray-500)' }}>
                        {expired ? '⚠️ Expirado — cliente não pagou a tempo' : `Expira em ${formatDateTime(d.expires_at)}`}
                      </div>
                    )}
                    {d.paid_at && (
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        Confirmado em {formatDateTime(d.paid_at)}
                      </div>
                    )}

                    {/* Ações */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      {d.status === 'pending' && (
                        <button className="btn btn-primary btn-sm" onClick={() => handleConfirm(d)}>
                          ✓ Confirmar recebimento
                        </button>
                      )}
                      {d.status === 'paid' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleRefund(d)}>
                          Marcar reembolsado
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ─────────── Config ─────────── */}
      {tab === 'config' && (
        <div style={{ maxWidth: 560 }}>
          {cfgLoading ? (
            <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
          ) : !canManage ? (
            <div className="empty-state">
              <p>Você não tem permissão pra alterar a chave PIX.</p>
            </div>
          ) : (
            <form onSubmit={handleSaveCfg}>
              <div style={{
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--gray-700)',
              }}>
                <strong style={{ color: 'var(--primary-dark)' }}>💳 Como funciona</strong>
                <div style={{ marginTop: 4, lineHeight: 1.55 }}>
                  Os sinais são pagos direto na sua conta bancária — a AtendoApp não intermedia o dinheiro.
                  Ao confirmar cada recebimento aqui, o agendamento do cliente é liberado.
                </div>
              </div>

              <div className="form-group">
                <label>Tipo de chave PIX *</label>
                <select
                  value={cfg.pix_key_type || 'cpf'}
                  onChange={e => setCfg(c => ({ ...c, pix_key_type: e.target.value }))}
                >
                  {KEY_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Chave PIX *</label>
                <input
                  value={cfg.pix_key || ''}
                  onChange={e => setCfg(c => ({ ...c, pix_key: e.target.value }))}
                  placeholder={
                    cfg.pix_key_type === 'cpf' ? 'Apenas 11 dígitos (sem pontuação)' :
                    cfg.pix_key_type === 'cnpj' ? 'Apenas 14 dígitos' :
                    cfg.pix_key_type === 'email' ? 'seu@email.com' :
                    cfg.pix_key_type === 'phone' ? 'DDD + número' :
                    'Cole a chave gerada pelo seu banco'
                  }
                />
              </div>

              <div className="form-group">
                <label>Nome do titular *</label>
                <input
                  value={cfg.pix_holder_name || ''}
                  onChange={e => setCfg(c => ({ ...c, pix_holder_name: e.target.value }))}
                  placeholder="Aparece no app do cliente ao pagar"
                  maxLength={200}
                />
                <small>Use o nome exatamente como está na sua conta bancária.</small>
              </div>

              <div className="form-group">
                <label>Cidade do titular *</label>
                <input
                  value={cfg.pix_city || ''}
                  onChange={e => setCfg(c => ({ ...c, pix_city: e.target.value }))}
                  placeholder="Ex: São Paulo, Belo Horizonte, Curitiba..."
                  maxLength={100}
                  required
                />
                <small>Cidade onde você mora ou o negócio está registrado. Aparece no comprovante do cliente.</small>
              </div>

              <div className="form-group">
                <label>Tempo de expiração do sinal (minutos)</label>
                <input
                  type="number"
                  min={5} max={1440}
                  value={cfg.deposit_expire_minutes || 60}
                  onChange={e => setCfg(c => ({ ...c, deposit_expire_minutes: parseInt(e.target.value) || 60 }))}
                />
                <small>Se o cliente não pagar nesse tempo, o agendamento é marcado como expirado e o horário volta a ficar livre.</small>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>
                  {saving ? '' : 'Salvar'}
                </button>
              </div>
            </form>
          )}

          {/* ── Confirmação automática (subconta white-label) ──── */}
          {canManage && (
            <div style={{ marginTop: 32, borderTop: '1px solid var(--gray-200)', paddingTop: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--gray-900)', margin: '0 0 8px' }}>
                ⚡ Confirmação automática
              </h3>

              {autoConfirm.active ? (
                <div style={{
                  background: '#dcfce7', border: '1px solid #86efac', borderRadius: 10,
                  padding: '14px 18px', fontSize: 13,
                }}>
                  <strong style={{ color: '#166534' }}>Ativada</strong> — quando seu cliente pagar o sinal, o agendamento é liberado automaticamente na hora. Estornos também são automáticos.
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8 }}>
                    Status: {autoConfirm.status === 'approved' ? '✅ Aprovado' : autoConfirm.status === 'pending' ? '⏳ Em análise' : autoConfirm.status}
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ color: 'var(--gray-600)', fontSize: 13, lineHeight: 1.6, margin: '0 0 12px' }}>
                    Hoje você confirma cada sinal recebido manualmente. Quer que o sistema faça isso automático?
                    Preencha seus dados abaixo — precisamos deles pra ativar o recebimento formal.
                  </p>

                  <div style={{
                    background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8,
                    padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#9a3412',
                  }}>
                    💡 <strong>Taxa do gateway:</strong> a cada sinal recebido, é cobrada uma taxa de aproximadamente <strong>R$ 0,99</strong> do processador de pagamento.
                    Esse valor é descontado automaticamente do que você recebe.
                    <br />Ex: sinal de R$ 20,00 → você recebe <strong>R$ 19,01</strong>.
                  </div>

                  <form onSubmit={handleActivate}>
                    <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div className="form-group">
                        <label>CPF ou CNPJ *</label>
                        <input value={autoForm.cpf_cnpj} onChange={e => setAutoForm(f => ({ ...f, cpf_cnpj: e.target.value }))} placeholder="Apenas números" required />
                      </div>
                      <div className="form-group">
                        <label>Data de nascimento *</label>
                        <input type="date" value={autoForm.birth_date} onChange={e => setAutoForm(f => ({ ...f, birth_date: e.target.value }))} required />
                      </div>
                      <div className="form-group">
                        <label>Tipo</label>
                        <select value={autoForm.company_type} onChange={e => setAutoForm(f => ({ ...f, company_type: e.target.value }))}>
                          {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Faturamento mensal estimado</label>
                        <MoneyInput value={autoForm.income_value} onChange={v => setAutoForm(f => ({ ...f, income_value: v }))} min={0} />
                      </div>
                    </div>

                    <div style={{ fontWeight: 600, fontSize: 13, margin: '16px 0 8px', color: 'var(--gray-700)' }}>
                      Endereço
                    </div>
                    <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                      <div className="form-group">
                        <label>Rua *</label>
                        <input value={autoForm.address_street} onChange={e => setAutoForm(f => ({ ...f, address_street: e.target.value }))} required />
                      </div>
                      <div className="form-group">
                        <label>Número *</label>
                        <input value={autoForm.address_number} onChange={e => setAutoForm(f => ({ ...f, address_number: e.target.value }))} required />
                      </div>
                    </div>
                    <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                      <div className="form-group">
                        <label>Complemento</label>
                        <input value={autoForm.address_complement} onChange={e => setAutoForm(f => ({ ...f, address_complement: e.target.value }))} placeholder="Apto, sala..." />
                      </div>
                      <div className="form-group">
                        <label>Bairro</label>
                        <input value={autoForm.address_neighborhood} onChange={e => setAutoForm(f => ({ ...f, address_neighborhood: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8 }}>
                      <div className="form-group">
                        <label>CEP *</label>
                        <input value={autoForm.address_postal_code} onChange={e => setAutoForm(f => ({ ...f, address_postal_code: e.target.value }))} placeholder="00000-000" required />
                      </div>
                      <div className="form-group">
                        <label>Cidade</label>
                        <input value={autoForm.address_city} onChange={e => setAutoForm(f => ({ ...f, address_city: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label>UF</label>
                        <select value={autoForm.address_state} onChange={e => setAutoForm(f => ({ ...f, address_state: e.target.value }))}>
                          <option value="">--</option>
                          {UF_LIST.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                      <button type="submit" className={`btn btn-primary${activating ? ' btn-loading' : ''}`} disabled={activating}>
                        {activating ? '' : '⚡ Ativar confirmação automática'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
